import * as React from 'react'
import {
  ICustomerWithHistory,
  IBaseLoyaltyTier,
  API,
  PermissionNames,
} from '@getgreenline/homi-shared'
import { LoyaltyPoints } from '../LoyaltyPoints'
import { CurrentCompanyStore } from '../../stores/CurrentCompanyStore'
import { inject, observer } from 'mobx-react'
import { CurrencyInput } from '../CurrencyInput'
import { COLORS } from '../../constants/Colors'
import { CustomersApi } from '@getgreenline/customers'
import { message, Spin } from 'antd-v4'
import { parseErrorMsg } from '../../utilities/helpers'
import { isUndefined } from 'lodash'
import { browserHistory } from 'react-router'
import { CustomerTotalSpend } from '../CustomerTotalSpend'
import { LoadingOutlined } from '@ant-design/icons'
import { ExternalLoyaltyAPI } from '@getgreenline/external-loyalty'
import Endpoints from '../../constants/Endpoints'

interface InjectedProps {
  currentCompanyStore?: CurrentCompanyStore
}

interface IProps {
  canUseExternalLoyalty?: boolean
  canViewCustomerTotalSpend: boolean
  customerWithHistory: ICustomerWithHistory
  matchingLoyaltyTier: IBaseLoyaltyTier | undefined
  fetchCustomer: () => Promise<void>
  loadCustomers?: () => Promise<void>
  failedToLoadExternalLoyaltyPoints: boolean
}

interface State {
  isEditing: boolean
  hasEditPermission: boolean
  loyaltyBalance?: number
  isLoadingBalance: boolean
}
const antIcon = <LoadingOutlined spin className='mr-1' />

@inject('currentCompanyStore')
@observer
export class CustomerHistorySection extends React.Component<IProps & InjectedProps, State> {
  readonly company = this.props.currentCompanyStore!.company!
  readonly canUseExternalLoyalty = this.props.currentCompanyStore!.canUseExternalLoyalty

  constructor(props: IProps & InjectedProps) {
    super(props)
    this.state = {
      isEditing: false,
      hasEditPermission: false,
      loyaltyBalance: undefined,
      isLoadingBalance: false,
    }
  }

  async componentDidMount() {
    const {
      customerWithHistory: { customer },
    } = this.props

    if (this.company.permissions.canAccessLoyalty || this.canUseExternalLoyalty) {
      this.setState({ loyaltyBalance: customer.creditBalance })
    }

    await this.checkPermission()
  }

  get isFromDashboard() {
    return browserHistory
      .getCurrentLocation()
      .pathname.includes(`/company/${this.company.id}/dashboard`)
  }

  get canEditLoyaltyBalance() {
    const { matchingLoyaltyTier } = this.props
    const { hasEditPermission } = this.state

    return hasEditPermission && this.isFromDashboard
  }

  async checkPermission() {
    // checking the permission here instead of injecting currentUserStore because this component is being used
    // in both Dashboard and POS, but currentUserStore is not available in POS (and due to the complexity of the current component hierarchy)
    if (!this.isFromDashboard) {
      return
    }

    try {
      const user = await API.getMe()
      const userPermissions = await API.getMyPermissions(user.id)

      const permissionFound = userPermissions.find(
        (userPermission) =>
          userPermission.permission.name === PermissionNames.CAN_ADJUST_LOYALTY_BALANCE,
      )

      this.setState({ hasEditPermission: !isUndefined(permissionFound) })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error')
    }
  }

  fetchCustomer = async () => {
    try {
      this.setState({ isLoadingBalance: true })
      await this.props.fetchCustomer()
    } finally {
      this.setState({ isLoadingBalance: false })
    }
  }

  async updateLoyaltyBalance() {
    this.setState({ isLoadingBalance: true })
    const {
      customerWithHistory: { customer },
      fetchCustomer,
      loadCustomers,
    } = this.props
    const { loyaltyBalance } = this.state

    if (isUndefined(loyaltyBalance)) {
      message.warning('Please enter the balance')
      return
    }

    try {
      if (this.canUseExternalLoyalty) {
        const { email, id, phone } = customer
        await ExternalLoyaltyAPI.updateExternalLoyaltyPoints({
          gatewayUrl: Endpoints.PLATFORM_GATEWAY_URL,
          merchantId: `blc-${this.company.id}`,
          customer: { email, id, phone },
          pointsToAdd: loyaltyBalance - customer.creditBalance,
        })
      } else {
        await CustomersApi.patchCustomer(this.company.id, customer.id, {
          creditBalance: loyaltyBalance,
          loyaltyTierName: this.props.customerWithHistory.customer.loyaltyTierName,
          loyaltyTierId: this.props.customerWithHistory.customer.loyaltyTierId,
        })
      }

      await fetchCustomer()
      loadCustomers && (await loadCustomers())
      message.success('Updated')
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error')
    }

    this.setState({ isEditing: false })
    this.setState({ isLoadingBalance: false })
  }

  renderLoyaltyBalance() {
    const {
      failedToLoadExternalLoyaltyPoints,
      customerWithHistory: { customer },
    } = this.props
    const { isEditing, loyaltyBalance } = this.state

    if (!this.canEditLoyaltyBalance) {
      return (
        <div className='d-flex justify-content-between'>
          {failedToLoadExternalLoyaltyPoints && this.canUseExternalLoyalty ? (
            <>
              {this.state.isLoadingBalance ? (
                <Spin className='mr-1' size='small' indicator={antIcon} />
              ) : (
                <>
                  <span>Points unavailable</span>
                  <a className='mr-1' onClick={this.fetchCustomer}>
                    Retry
                  </a>
                </>
              )}
            </>
          ) : (
            <LoyaltyPoints loyaltyBalance={customer.creditBalance} />
          )}
        </div>
      )
    }

    return isEditing ? (
      <div className='d-flex flex-column'>
        <CurrencyInput
          className='ml-3 pl-2 pb-0 gr-currency-input'
          centValue={loyaltyBalance}
          onChange={(value) => this.setState({ loyaltyBalance: value })}
          onlyPositiveNumber
          usePoints={true}
        />
      </div>
    ) : (
      <div className='d-flex justify-content-between'>
        {failedToLoadExternalLoyaltyPoints && this.canUseExternalLoyalty ? (
          <>
            {this.state.isLoadingBalance ? (
              <Spin className='mr-1' size='small' indicator={antIcon} />
            ) : (
              <>
                <span>Points unavailable</span>
                <a className='mr-1' onClick={this.fetchCustomer}>
                  Retry
                </a>
              </>
            )}
          </>
        ) : (
          <>
            <LoyaltyPoints loyaltyBalance={customer.creditBalance} />
            <a
              className='mr-1'
              onClick={() => {
                this.setState({ isEditing: true })
              }}
            >
              Edit
            </a>
          </>
        )}
      </div>
    )
  }

  renderLoyaltyBalanceButtons() {
    const {
      customerWithHistory: { customer },
    } = this.props
    const { isEditing } = this.state

    if (!this.canEditLoyaltyBalance || !isEditing) {
      return
    }

    return (
      <div className='d-flex justify-content-end mr-3'>
        {this.state.isLoadingBalance ? (
          <Spin className='mr-3 mt-1' indicator={antIcon} />
        ) : (
          <>
            <a
              className='mr-2'
              onClick={() => {
                this.setState({ isEditing: false, loyaltyBalance: customer.creditBalance })
              }}
              style={{ color: COLORS.darkBlack }}
            >
              Cancel
            </a>
            <a
              onClick={() => {
                this.updateLoyaltyBalance()
              }}
            >
              Save
            </a>
          </>
        )}
      </div>
    )
  }

  render() {
    const {
      customerWithHistory: { customer, statistics },
      matchingLoyaltyTier,
    } = this.props
    const { isEditing } = this.state

    return (
      <>
        <h5>Purchase history</h5>
        <br />
        <table className='table table-striped mb-0'>
          <tbody>
            <tr>
              <td>Total spend</td>
              <td style={isEditing ? { paddingLeft: '35.5px' } : {}}>
                <CustomerTotalSpend
                  canViewCustomerTotalSpend={this.props.canViewCustomerTotalSpend}
                  totalSpend={customer.totalSpend}
                />
              </td>
            </tr>
            <tr>
              <td>Last purchase</td>
              <td style={isEditing ? { paddingLeft: '35.5px' } : {}}>
                {statistics.lastPurchase
                  ? statistics.lastPurchase.map((purchase, index) => {
                      return (
                        <div key={index}>
                          {purchase.product.parentProductName
                            ? purchase.product.parentProductName + ' - ' + purchase.product.name
                            : purchase.product.name}
                        </div>
                      )
                    })
                  : 'No purchases yet'}
              </td>
            </tr>
            {this.props.canUseExternalLoyalty ? (
              <>
                <tr>
                  <td className='customer-purchase-table'>Loyalty balance</td>
                  <td>{this.renderLoyaltyBalance()}</td>
                </tr>
              </>
            ) : (
              this.company.permissions.canAccessLoyalty && (
                <>
                  <tr>
                    <td>Loyalty tier</td>
                    <td style={isEditing ? { paddingLeft: '35.5px' } : {}}>
                      {matchingLoyaltyTier ? matchingLoyaltyTier.name : '-'}
                    </td>
                  </tr>
                  <tr>
                    <td className='customer-purchase-table'>Loyalty balance</td>
                    <td>{this.renderLoyaltyBalance()}</td>
                  </tr>
                </>
              )
            )}
          </tbody>
        </table>

        {this.renderLoyaltyBalanceButtons()}

        <br />
      </>
    )
  }
}
